import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { TrendingUp, Wallet, ShieldCheck, Clock } from "lucide-react";

const TIER_COLOR = {
  preferred: "bg-emerald-100 text-emerald-700",
  standard: "bg-blue-100 text-blue-700",
  watch: "bg-amber-100 text-amber-800",
};

const STATUS_COLORS = {
  pending_vendor_acceptance: "#f59e0b",
  vendor_accepted: "#3b82f6",
  consumer_engaged: "#06b6d4",
  intake_completed: "#22d3ee",
  fulfillment_in_progress: "#8b5cf6",
  completed: "#10b981",
  invoice_submitted: "#84cc16",
  payment_pending: "#eab308",
  paid: "#059669",
  cancelled: "#9ca3af",
};

export default function VendorAnalytics() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/vendor/me/stats"), api.get("/vendor/me/activity")])
      .then(([s, a]) => {
        setStats(s.data || {});
        setActivity(a.data || []);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const statusBreakdown = useMemo(() => {
    const c = {};
    activity.forEach((it) => {
      const k = it.status || "unknown";
      c[k] = (c[k] || 0) + 1;
    });
    return Object.entries(c).map(([name, value]) => ({
      name,
      value,
      color: STATUS_COLORS[name] || "#94a3b8",
    }));
  }, [activity]);

  const reliabilityChart = useMemo(() => {
    if (!stats) return [];
    return [
      { metric: "Acceptance", value: stats.acceptance_rate || 0 },
      { metric: "Fulfillment", value: stats.fulfillment_rate || 0 },
      { metric: "Reliability", value: stats.reliability_score || 0 },
    ];
  }, [stats]);

  if (loading) {
    return <div className="text-sm text-slate-500">Loading analytics…</div>;
  }

  return (
    <div className="space-y-6" data-testid="vendor-analytics-page">
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-700">
          Vendor · Analytics
        </div>
        <div className="flex flex-wrap items-center gap-3 mt-1">
          <h1 className="text-3xl font-bold text-slate-900">Operational performance</h1>
          {stats?.tier && (
            <span
              className={`text-[10px] uppercase tracking-wide font-bold px-2.5 py-1 rounded-full ${
                TIER_COLOR[stats.tier] || TIER_COLOR.standard
              }`}
              data-testid="vendor-analytics-tier"
            >
              {stats.tier} tier
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={<TrendingUp className="w-4 h-4" />}
          label="Active workflows"
          value={stats?.active_workflows ?? 0}
          accent="emerald"
        />
        <KpiCard
          icon={<Clock className="w-4 h-4" />}
          label="Pending acceptance"
          value={stats?.pending_acceptance ?? 0}
          accent="amber"
        />
        <KpiCard
          icon={<Wallet className="w-4 h-4" />}
          label="Revenue (30d)"
          value={`$${Number(stats?.revenue_30d || 0).toLocaleString()}`}
          accent="blue"
        />
        <KpiCard
          icon={<ShieldCheck className="w-4 h-4" />}
          label="Reliability"
          value={`${stats?.reliability_score ?? 0}/100`}
          accent="violet"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <article className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Operational scorecard</h3>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={reliabilityChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="metric" tick={{ fontSize: 12, fill: "#475569" }} />
                <YAxis tick={{ fontSize: 12, fill: "#475569" }} domain={[0, 100]} />
                <Tooltip />
                <Bar dataKey="value" fill="#10b981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="text-xs text-slate-500 mt-2">
            Blended from last-90-day acceptance + fulfilment performance.
          </div>
        </article>

        <article className="rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Workflow status mix</h3>
          {statusBreakdown.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-sm text-slate-500">
              No recent activity to chart yet.
            </div>
          ) : (
            <div className="h-56">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={statusBreakdown}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={45}
                    outerRadius={75}
                  >
                    {statusBreakdown.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend
                    iconSize={8}
                    wrapperStyle={{ fontSize: 11 }}
                    formatter={(v) => v.replace(/_/g, " ")}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </article>
      </div>

      <article className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Receivables snapshot</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Stat label="MTD revenue" value={`$${Number(stats?.revenue_mtd || 0).toLocaleString()}`} />
          <Stat label="30d revenue" value={`$${Number(stats?.revenue_30d || 0).toLocaleString()}`} />
          <Stat label="Unpaid aging" value={`$${Number(stats?.unpaid_aging || 0).toLocaleString()}`} />
          <Stat label="Unpaid count" value={stats?.unpaid_count ?? 0} />
        </div>
      </article>
    </div>
  );
}

function KpiCard({ icon, label, value, accent }) {
  const ACC = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    amber: "text-amber-700 bg-amber-50 border-amber-200",
    blue: "text-blue-700 bg-blue-50 border-blue-200",
    violet: "text-violet-700 bg-violet-50 border-violet-200",
  };
  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-4">
      <div
        className={`inline-flex items-center justify-center w-7 h-7 rounded-lg border ${
          ACC[accent] || ACC.emerald
        }`}
      >
        {icon}
      </div>
      <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mt-3">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-0.5">{value}</div>
    </article>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/40 p-3">
      <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">{label}</div>
      <div className="text-lg font-bold text-slate-900 mt-0.5">{value}</div>
    </div>
  );
}
